import { useMemo, useState } from "react";
import type { MatchSpec } from "@howcomet/shared";
import { CheckFrame } from "./CheckFrame";
import { seededShuffle } from "./shuffle";
import type { ShellProps } from "./registry";

/**
 * The fourth shell. It exists to prove the plugin path end to end: this file
 * plus one `registerShell` call in the shared package was the entire cost of
 * adding a new kind of check. No pipeline, prompt, validator or schema change.
 */
export function MatchShell({ spec, onDone, collecting }: ShellProps<MatchSpec>) {
  // Shuffle the right column, seeded off the prompt so a retry is stable.
  const rightColumn = useMemo(
    () =>
      seededShuffle(
        spec.pairs.map((pair, index) => ({ text: pair.right, index })),
        spec.prompt,
      ),
    [spec],
  );

  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [links, setLinks] = useState<Record<number, number>>({});
  const [misses, setMisses] = useState(0);

  const complete = Object.keys(links).length === spec.pairs.length;
  const correct = complete && spec.pairs.every((_, index) => links[index] === index);
  const outcome = complete ? (correct ? "right" : "wrong") : null;

  const usedRights = new Set(Object.values(links));

  return (
    <CheckFrame
      spec={spec}
      outcome={outcome}
      onRetry={() => {
        setMisses((m) => m + 1);
        setLinks({});
        setSelectedLeft(null);
      }}
      onCollect={() => onDone({ correct: true, firstTry: misses === 0 })}
      collecting={collecting}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-3">
          {spec.pairs.map((pair, index) => {
            const linked = links[index] !== undefined;
            const active = selectedLeft === index;
            return (
              <button
                key={pair.left}
                disabled={complete}
                onClick={() => {
                  if (linked) {
                    // Tapping a linked item unlinks it, which is how a child
                    // fixes a mistake without clearing the whole board.
                    setLinks(({ [index]: _removed, ...rest }) => rest);
                    setSelectedLeft(index);
                  } else {
                    setSelectedLeft(active ? null : index);
                  }
                }}
                className={`kidbtn rounded-2xl px-3 py-4 text-left text-[15px] font-bold ${
                  active
                    ? "bg-gold text-night"
                    : linked
                      ? "bg-lilac text-night"
                      : "bg-panel text-cream"
                }`}
              >
                {pair.left}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3">
          {rightColumn.map((option) => {
            const taken = usedRights.has(option.index);
            return (
              <button
                key={option.text}
                disabled={complete || taken}
                onClick={() => {
                  if (selectedLeft === null) return;
                  setLinks((l) => ({ ...l, [selectedLeft]: option.index }));
                  setSelectedLeft(null);
                }}
                className={`kidbtn rounded-2xl px-3 py-4 text-left text-[15px] font-bold ${
                  taken ? "bg-lilac text-night" : "bg-panel text-cream"
                } ${selectedLeft === null && !taken ? "opacity-60" : ""}`}
              >
                {option.text}
              </button>
            );
          })}
        </div>
      </div>

      {!complete && (
        <p className="mt-3 text-[14px] text-cream/55">
          {selectedLeft === null ? "Tap something on the left first." : "Now tap its partner."}
        </p>
      )}
    </CheckFrame>
  );
}
